import React, { useEffect } from 'react';
import Button from '../../components/Button';
import { formatDate, formatDateTime, formatNumber } from '../../utils/helpers';
import { changeTypeLabel, formatINR, methodLabel } from './manualRegistrationUtils';
import { EyeOpenIcon } from './ProductAdminIcons';
import t from './AdminTable.module.css';
import m from './ManualRegistrations.module.css';

const STATUS_BADGE = {
  active: ['Active', 'badgeSuccess'],
  expired: ['Expired', 'badgeDanger'],
  closed: ['Closed', 'badgeNeutral'],
  cancelled: ['Cancelled', 'badgeNeutral'],
};

// Status of a payment's subscription (Active, Expired, Closed or Cancelled)
export function RegistrationStatusBadge({ status }) {
  const [label, tone] = STATUS_BADGE[status] || STATUS_BADGE.active;
  return <span className={`${t.badge} ${t[tone]}`}>{label}</span>;
}

// Subscription state of an account: trial/paid, active/expired
export function SubscriptionStateBadge({ state, label }) {
  const tone = state === 'paid_active' ? t.badgeSuccess
    : state === 'trial_active' ? t.badgeInfo
      : state === 'none' ? t.badgeNeutral : t.badgeDanger;
  return <span className={`${t.badge} ${tone}`}>{label}</span>;
}

// Receipt (image or PDF) in a dialog. `receipt` is { name, type, url } or null.
export function ReceiptModal({ receipt, onClose }) {
  // A receipt shown from a temporary object URL is released when the dialog closes
  useEffect(() => {
    const url = receipt && receipt.url;
    return () => { if (url && url.startsWith('blob:') && !receipt.keepUrl) URL.revokeObjectURL(url); };
  }, [receipt]);

  useEffect(() => {
    if (!receipt) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [receipt, onClose]);

  if (!receipt) return null;
  const isPdf = receipt.type === 'application/pdf';
  return (
    <div className={m.modalBackdrop} onClick={onClose} role="presentation">
      <div className={m.modal} role="dialog" aria-modal="true" aria-label="Payment receipt" onClick={(e) => e.stopPropagation()}>
        <div className={m.modalHead}>
          <strong title={receipt.name}>{receipt.name || 'Payment receipt'}</strong>
          <Button size="sm" variant="secondary" onClick={onClose}>Close</Button>
        </div>
        <div className={m.modalBody}>
          {receipt.loading && <div className={m.modalEmpty}>Loading the receipt…</div>}
          {receipt.error && <div className={m.modalEmpty}>{receipt.error}</div>}
          {!receipt.loading && !receipt.error && !receipt.url && (
            <div className={m.modalEmpty}>
              The receipt file is not available in this demo.
              <br />
              Receipts uploaded in this session can be viewed here.
            </div>
          )}
          {receipt.url && (isPdf
            ? <iframe title="Payment receipt" src={receipt.url} />
            : <img src={receipt.url} alt="Payment receipt" />)}
        </div>
      </div>
    </div>
  );
}

const STATE_BADGE = {
  active: ['Active', 'badgeSuccess', 'dotGreen'],
  expired: ['Expired', 'badgeDanger', 'dotRed'],
  closed: ['Closed', 'badgeNeutral', 'dotMuted'],
  cancelled: ['Cancelled', 'badgeNeutral', 'dotMuted'],
};

const KIND_LABEL = { trial: 'Free trial', razorpay: 'Online payment', manual: 'Manual payment' };

const SOURCE_LABEL = { trial: 'Free trial', razorpay: 'Online (application)', manual: 'Manual' };

// Compact payment and subscription history of one company: every plan it has had, what was paid and how
export function CompanyPaymentHistory({ items, loading, error }) {
  if (loading) return <p className={t.muted}>Loading history…</p>;
  if (error) return <div className={`${m.banner} ${m.bannerError}`}>{error}</div>;
  if (!items?.length) return <div className={m.timelineNote}>No subscription or payment has been recorded for this company yet.</div>;

  const paid = items.filter((item) => item.kind !== 'trial' && item.state !== 'cancelled');
  const total = paid.reduce((sum, item) => sum + (item.amount || 0), 0);
  return (
    <>
      <div className={m.historyTotal}>
        {paid.length
          ? <>{paid.length} paid subscription{paid.length === 1 ? '' : 's'} · <strong>{formatINR(total)}</strong> paid in total (online and manual)</>
          : 'No paid subscription yet: only a free trial.'}
      </div>
      <div className={t.wrap}>
        <table className={t.table}>
          <thead>
            <tr>
              <th>Plan</th>
              <th className={`${t.wStat} ${t.num}`}>Amount</th>
              <th className={`${t.wPlan} ${t.hideSm}`}>Paid via</th>
              <th className={`${t.wDate} ${t.hideSm}`}>Date</th>
              <th className={`${t.wDateTime} ${t.hideXs}`}>Period</th>
              <th className={`${t.wStatus}`}>Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const [stateLabel, stateBadge] = STATE_BADGE[item.state] || STATE_BADGE.closed;
              const isTrial = item.kind === 'trial';
              const period = `${formatDate(item.start)} → ${formatDate(item.expiry)}`;
              return (
                <tr key={item.id}>
                  <td>
                    <div className={t.stack}>
                      <span className={t.name} title={item.plan_name}>{item.plan_name}</span>
                      <span className={t.muted} title={`${item.licences} licence${item.licences === 1 ? '' : 's'}`}>
                        {item.licences} licence{item.licences === 1 ? '' : 's'}
                      </span>
                    </div>
                  </td>
                  <td className={`${t.num} ${isTrial ? t.muted : t.accent}`} title={isTrial ? 'Free' : formatINR(item.amount)}>
                    {isTrial ? '—' : formatINR(item.amount)}
                  </td>
                  <td className={t.hideSm} title={SOURCE_LABEL[item.kind] || item.kind}>{SOURCE_LABEL[item.kind] || item.kind}</td>
                  <td className={t.hideSm} title={formatDate(item.created_at)}>{formatDate(item.created_at)}</td>
                  <td className={t.hideXs} title={period}>{period}</td>
                  <td className={t.fit}><span className={`${t.badge} ${t[stateBadge]}`}>{stateLabel}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// Subscription & payment history of one company, newest first
export function SubscriptionHistory({ items, loading, error, onViewReceipt }) {
  if (loading) return <p className={t.muted}>Loading history…</p>;
  if (error) return <div className={`${m.banner} ${m.bannerError}`}>{error}</div>;
  if (!items?.length) {
    return (
      <div className={m.timelineNote}>No subscription has been recorded for this company yet.</div>
    );
  }
  return (
    <ol className={m.timeline}>
      {items.map((item) => {
        const [stateLabel, stateBadge, dot] = STATE_BADGE[item.state] || STATE_BADGE.closed;
        const isPaid = item.kind !== 'trial';
        return (
          <li className={m.timelineItem} key={item.id}>
            <span className={`${m.dot} ${m[dot] || ''}`} aria-hidden="true" />
            <div className={m.timelineCard}>
              <div className={m.timelineHead}>
                <div className={m.timelineTitle}>
                  <span>{item.plan_name}</span>
                  <span className={`${t.badge} ${t[stateBadge]}`}>{stateLabel}</span>
                  {item.change_type && item.kind !== 'trial' && (
                    <span className={`${t.badge} ${t.badgeInfo}`}>{changeTypeLabel(item.change_type)}</span>
                  )}
                </div>
                <span className={m.timelineMeta}>{KIND_LABEL[item.kind] || item.kind} · {formatDateTime(item.created_at)}</span>
              </div>

              <dl className={m.facts}>
                <div className={m.fact}><dt>Period</dt><dd>{formatDate(item.start)} → {formatDate(item.expiry)}</dd></div>
                <div className={m.fact}><dt>Licences</dt><dd>{formatNumber(item.licences)}</dd></div>
                <div className={m.fact}>
                  <dt>Documents</dt>
                  <dd>{formatNumber(item.documents_used)}{item.document_limit ? ` / ${formatNumber(item.document_limit)}` : ''}</dd>
                </div>
                {item.previous_plan_name && (
                  <div className={m.fact}><dt>Previous plan</dt><dd>{item.previous_plan_name}</dd></div>
                )}
                {isPaid && (
                  <>
                    <div className={m.fact}><dt>Amount</dt><dd>{formatINR(item.amount)}</dd></div>
                    <div className={m.fact}><dt>Method</dt><dd>{item.method === 'razorpay' ? 'Razorpay' : methodLabel(item.method)}</dd></div>
                    <div className={m.fact}><dt>Reference</dt><dd>{item.reference || '—'}</dd></div>
                  </>
                )}
                {item.actor && <div className={m.fact}><dt>{item.kind === 'manual' ? 'Activated by' : 'Source'}</dt><dd>{item.actor}</dd></div>}
              </dl>

              {item.close_reason && (
                <div className={m.timelineNote}>Closed as “{item.close_reason}”. Its record is kept.</div>
              )}
              {item.kind === 'manual' && Number.isFinite(item.snapshot_of_previous) && (
                <div className={m.timelineNote}>
                  Usage snapshot before this change: {formatNumber(item.snapshot_of_previous)} documents on the previous subscription
                  {item.tokens_moved ? `; ${formatNumber(item.tokens_moved)} licence token${item.tokens_moved === 1 ? '' : 's'} moved to this subscription` : ''}.
                </div>
              )}
              {item.kind === 'manual' && item.has_receipt && onViewReceipt && (
                <div>
                  <Button size="sm" variant="secondary" className={t.softButton} onClick={() => onViewReceipt(item)}>
                    <span className={m.buttonContent}><EyeOpenIcon size={14} /> View receipt</span>
                  </Button>
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
